import { useEffect, useState } from 'react';
import { View, Text, TextInput, Pressable, StyleSheet } from 'react-native';
import { Check } from 'lucide-react-native';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY, withWeight } from '../../theme';
import { CURSOR_POINTER } from '../../utils/webStyles';
import { useWebHover } from '../../hooks/useWebHover';
import { Button } from '../common/Button';
import { SegmentedControl } from '../common/SegmentedControl';
import { TextLink } from '../common/TextLink';
import { useDefensePlansStore } from '../../store/defensePlansStore';
import { ASSIGNMENT_STATUS_LABEL } from '../../utils/planAssignmentSummary';
import type { PlanAssignmentStatus } from '../../types';

// One person in the Assign list (Oct 2026, Glenn: "make it look like my Team
// list"). The header is the whole click target and toggles the assignment;
// a ticked person opens in place to show what they do, their status, the
// address for plan updates and, in a bundle, the leader control. The detail
// is a sibling of the header Pressable, not a child, so its fields don't sit
// inside another button.

const STATUS_OPTIONS = (['asked', 'accepted', 'done', 'declined'] as const).map((key) => ({
  key,
  label: ASSIGNMENT_STATUS_LABEL[key],
}));

/** An ink checkbox: the choice shows as a check, never as a color. */
function Checkbox({ checked }: { checked: boolean }) {
  const { colors } = useTheme();
  return (
    <View
      style={[
        styles.checkbox,
        {
          borderColor: checked ? colors.text : colors.textTertiary,
          backgroundColor: checked ? colors.text : 'transparent',
        },
      ]}>
      {checked && <Check size={12} color={colors.background} />}
    </View>
  );
}

export interface AssignRowDetail {
  planId: string;
  partyId: string;
  /** What they do, as stored. */
  description: string | undefined;
  /** Null when a bundle teammate's links disagree. */
  status: PlanAssignmentStatus | null;
  /** Text shown for a mixed status ("1 done, 1 asked"). */
  mixedText?: string;
  /** An address from the person's list, offered but never stored unasked. */
  suggestedEmail?: string;
  onDescription: (description: string | undefined) => void;
  onStatus: (status: PlanAssignmentStatus) => void;
  /** Bundle mode only. */
  leader?: { isLeader: boolean; onChange: (next: boolean) => void };
}

function EmailField({
  planId,
  partyId,
  suggestedEmail,
}: Pick<AssignRowDetail, 'planId' | 'partyId' | 'suggestedEmail'>) {
  const { colors } = useTheme();
  const getPartyContactEmail = useDefensePlansStore((s) => s.getPartyContactEmail);
  const setPartyContactEmail = useDefensePlansStore((s) => s.setPartyContactEmail);
  // undefined until the read lands, so the suggestion never shows for a party
  // that already has an address.
  const [stored, setStored] = useState<string | null | undefined>(undefined);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getPartyContactEmail(planId, partyId)
      .then((email) => {
        if (cancelled) return;
        setStored(email ?? null);
        setDraft(email ?? '');
      })
      .catch(() => {
        if (!cancelled) setStored(null);
      });
    return () => {
      cancelled = true;
    };
  }, [planId, partyId, getPartyContactEmail]);

  // The only two paths that store an address: the suggestion tap and the
  // owner confirming what they typed. A Cloud Function emails every address
  // in a plan's partyContacts, so nothing else may write one.
  const save = async (value: string) => {
    setSaving(true);
    try {
      const ok = await setPartyContactEmail(planId, partyId, value.trim() || undefined);
      if (ok) {
        setStored(value.trim() || null);
        setDraft(value.trim());
      }
    } finally {
      setSaving(false);
    }
  };

  const changed = stored !== undefined && draft.trim() !== (stored ?? '');
  const showSuggestion = stored === null && !draft.trim() && !!suggestedEmail;

  return (
    <View>
      <Text style={[styles.fieldLabel, { color: colors.textSecondary }]}>Email for updates</Text>
      <TextInput
        value={draft}
        onChangeText={setDraft}
        onSubmitEditing={() => changed && void save(draft)}
        editable={stored !== undefined}
        placeholder="e.g. jane@example.org"
        placeholderTextColor={colors.textTertiary}
        keyboardType="email-address"
        autoCapitalize="none"
        testID={`assign-email-${partyId}`}
        accessibilityLabel="Email for updates"
        accessibilityHint="Only you can see this"
        style={[styles.input, { color: colors.text, borderColor: colors.border }]}
      />
      <View style={styles.emailActions}>
        {showSuggestion && (
          <TextLink
            label={`Use ${suggestedEmail}`}
            onPress={() => void save(suggestedEmail)}
            testID={`assign-email-suggest-${partyId}`}
            textStyle={styles.smallLink}
          />
        )}
        {changed && (
          <Button
            title="Save email"
            onPress={() => void save(draft)}
            variant="secondary"
            size="xs"
            loading={saving}
            testID={`assign-email-save-${partyId}`}
          />
        )}
        {!showSuggestion && !changed && (
          <Text style={[styles.hint, { color: colors.textSecondary }]}>Only you can see this.</Text>
        )}
      </View>
    </View>
  );
}

function RowDetail({ detail }: { detail: AssignRowDetail }) {
  const { colors } = useTheme();
  const { partyId, description } = detail;
  const [draft, setDraft] = useState(description ?? '');

  const commit = () => {
    const next = draft.trim();
    if (next !== (description ?? '')) detail.onDescription(next || undefined);
  };

  return (
    <View style={styles.detail}>
      <View>
        <Text style={[styles.fieldLabel, { color: colors.textSecondary }]}>What will they do</Text>
        <TextInput
          value={draft}
          onChangeText={setDraft}
          onBlur={commit}
          placeholder="e.g. Thin trees along the north fence by May"
          placeholderTextColor={colors.textTertiary}
          maxLength={200}
          testID={`assign-description-${partyId}`}
          accessibilityLabel="What this party will do"
          style={[styles.input, { color: colors.text, borderColor: colors.border }]}
        />
      </View>
      <View>
        <Text style={[styles.fieldLabel, { color: colors.textSecondary }]}>Status</Text>
        <SegmentedControl<PlanAssignmentStatus | 'mixed'>
          options={STATUS_OPTIONS}
          value={detail.status ?? 'mixed'}
          onChange={(next) => next !== 'mixed' && detail.onStatus(next)}
          ariaLabel="Status"
          role="radiogroup"
          testID={`assign-status-${partyId}`}
        />
        {detail.mixedText && (
          <Text style={[styles.hint, styles.mixed, { color: colors.textSecondary }]}>
            {detail.mixedText}
          </Text>
        )}
      </View>
      <EmailField planId={detail.planId} partyId={partyId} suggestedEmail={detail.suggestedEmail} />
    </View>
  );
}

export function AssignPersonRow({
  name,
  meta,
  checked,
  isLeader,
  onToggle,
  pickTestID,
  rowTestID,
  detail,
}: {
  name: string;
  meta: string;
  checked: boolean;
  isLeader: boolean;
  onToggle: () => void;
  pickTestID: string;
  /** Set on a ticked row in bundle mode (`assign-team-<partyId>`). */
  rowTestID?: string;
  /** Shown under the header while ticked; absent until the party exists. */
  detail: AssignRowDetail | null;
}) {
  const { colors } = useTheme();
  const { hovered, hoverProps } = useWebHover();
  const leader = detail?.leader;
  return (
    <View style={[styles.row, { borderColor: colors.border }]} testID={rowTestID}>
      <View style={styles.headerLine}>
        <Pressable
          onPress={onToggle}
          testID={pickTestID}
          accessibilityRole="checkbox"
          aria-checked={checked}
          accessibilityLabel={name}
          {...hoverProps}
          style={[
            styles.header,
            CURSOR_POINTER,
            { backgroundColor: hovered ? colors.hoverSurface : 'transparent' },
          ]}>
          <Checkbox checked={checked} />
          <View style={styles.headerText}>
            <Text style={[styles.name, { color: colors.text }]} numberOfLines={1}>
              {name}
            </Text>
            {meta.length > 0 && (
              <Text style={[styles.meta, { color: colors.textSecondary }]} numberOfLines={1}>
                {meta}
              </Text>
            )}
          </View>
          {isLeader && (
            <View
              style={[styles.leaderTag, { borderColor: colors.border }]}
              testID="assign-leader-tag">
              <Text style={[styles.leaderTagText, { color: colors.text }]}>Leader</Text>
            </View>
          )}
        </Pressable>
        {/* One click, on the row, next to the name (Glenn, Oct 8 2026). A
          sibling of the header Pressable, so it isn't a button in a button. */}
        {checked && leader && (
          <TextLink
            label={leader.isLeader ? 'Remove as leader' : 'Make leader'}
            onPress={() => leader.onChange(!leader.isLeader)}
            testID={`assign-leader-${detail.partyId}`}
            textStyle={styles.smallLink}
          />
        )}
      </View>
      {checked && detail && <RowDetail key={detail.partyId} detail={detail} />}
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  headerLine: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
  },
  header: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.smd,
    paddingVertical: SPACING.smd,
    borderRadius: BORDER_RADIUS.sm,
  },
  headerText: {
    flex: 1,
    minWidth: 0,
  },
  name: {
    ...TYPOGRAPHY.label,
  },
  meta: {
    ...TYPOGRAPHY.caption,
  },
  checkbox: {
    width: 18,
    height: 18,
    borderRadius: BORDER_RADIUS.xs,
    borderWidth: 1.5,
    alignItems: 'center',
    justifyContent: 'center',
  },
  leaderTag: {
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.full,
    paddingHorizontal: SPACING.sm,
    paddingVertical: SPACING.xxs,
  },
  leaderTagText: {
    ...withWeight(TYPOGRAPHY.caption, 'medium'),
  },
  // Lines up under the name, past the checkbox and its gap.
  detail: {
    gap: SPACING.sm,
    paddingLeft: 18 + SPACING.smd,
    paddingBottom: SPACING.md,
  },
  fieldLabel: {
    ...withWeight(TYPOGRAPHY.caption, 'medium'),
    marginBottom: SPACING.xxs,
  },
  input: {
    ...TYPOGRAPHY.bodySmall,
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.sm,
    paddingHorizontal: SPACING.smd,
    paddingVertical: SPACING.sm,
  },
  hint: {
    ...TYPOGRAPHY.caption,
  },
  mixed: {
    marginTop: SPACING.xxs,
  },
  emailActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
    marginTop: SPACING.xs,
  },
  smallLink: {
    ...withWeight(TYPOGRAPHY.caption, 'medium'),
  },
});
